import Route from '@ember/routing/route';
import type PublicTransition from '@ember/routing/transition';
import { later, next } from '@ember/runloop';
import { service } from '@ember/service';

import { hashSettled } from 'rsvp';

import type { Service } from 'webapp-types';
import { AppEvent } from 'webapp/enums/events';
import { TourId } from 'webapp/enums/tour';

import type DesignerController from 'webapp/controllers/designer';
import type DesignerBoardController from 'webapp/controllers/designer/board';
import type DesignerBoardBlockController from 'webapp/controllers/designer/board/block';

import { BoardStatus } from 'webapp/models-custom/board';
import type BoardRecord from 'webapp/models/board';

export default class DesignerBoardRoute extends Route {
	@service('infolayer') declare infolayerService: Service.Infolayer;
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('event-manager') declare eventManagerService: Service.EventManager;
	@service('popup') declare popupService: Service.Popup;
	@service('request') declare requestService: Service.Request;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('tour') declare tourService: Service.Tour;
	@service('model-manager/boards') declare mmBoardsService: Service.ModelManager.Boards;
	@service('model-manager/metrics') declare mmMetricsService: Service.ModelManager.Metrics;
	@service('model-manager/templates') declare mmTemplatesService: Service.ModelManager.Templates;
	@service('model-manager/board-loops')
	declare mmBoardLoopsService: Service.ModelManager.BoardLoops;
	@service('flow/tour/designer') declare flowTourDesignerService: Service.Flow.Tour.Designer;
	@service('store') declare storeService: Service.Store;
	@service('model-manager/blocks') declare mmBlocksService: Service.ModelManager.Blocks;

	constructor() {
		// eslint-disable-next-line prefer-rest-params
		super(...arguments);

		this.navigationService.router.on('routeWillChange', (transition) => {
			// An aborted transition (e.g. held by the leave guard) keeps the board on screen.
			if (transition.to && !transition.isAborted) {
				const controller = this.controllerFor('designer.board');
				if (!transition.to.name.startsWith('designer')) {
					this.eventManagerService.off(AppEvent.DESIGNER_BOARD_LOADED, controller);
				}

				// cleanup model if is a new record
				const boardRecord = controller.model as BoardRecord;
				if (boardRecord && !boardRecord.id && !boardRecord.isSaving) {
					boardRecord.unloadRecord();
				}
			}
		});
	}

	override beforeModel(): Promise<unknown> {
		const promises: Record<string, Promise<unknown>> = {};

		// cleanups right panel
		const designerBoardBlockController = this.controllerFor(
			'designer.board.block',
		) as DesignerBoardBlockController;
		designerBoardBlockController.selectBlock(null);

		promises.initial = this.navigationService.initialRequestsPromise;
		promises.boardAccountTemplates = this.mmTemplatesService.requestBoardAccountTemplates();
		promises.boardLoopAccountTemplates =
			this.mmTemplatesService.requestBoardLoopAccountTemplates();
		promises.reportAccountTemplates = this.mmTemplatesService.requestReportAccountTemplates(); // in case we are accessing board (via link) used in report account template, we need to get all boards used in report account templates
		promises.metrics = this.mmMetricsService.requestMetrics();
		promises.boardLoops = this.mmBoardLoopsService.requestBoardLoops();

		return hashSettled(promises);
	}

	override model(params: { board_id: string }): BoardRecord | null {
		let model: BoardRecord | undefined;

		if (params) {
			if (params.board_id === 'null') {
				this.navigationService.transitionToRoute('designer');
				return null;
			} else {
				model = this.storeService.peekRecord('board', params.board_id) as BoardRecord;
			}
		}

		if (!model || this.currentUserService.isDemo) {
			this.navigationService.replaceRoute('index');
			this.infolayerService.error(
				'Dashboard does not exist or you do not have permissions to customize it.',
			);
		} else {
			let modelObj = this.mmBoardsService.normalBoardModels.find(
				(item) => item.id === model.id,
			);
			if (!modelObj) {
				modelObj = this.mmBoardsService.createModelFromRecord(model);
			}
			if (!modelObj.canCurrentUserEdit) {
				this.infolayerService.error('You are not allowed to customize this dashboard.');
				this.navigationService.replaceRoute('index');
				return null;
			}
		}

		return model || null;
	}

	override afterModel(model: BoardRecord, transition: PublicTransition): void {
		const boardId = model.id;

		// reset newBadge
		model.newBadge = !boardId;

		if (boardId) {
			let toBlock = '';

			if (transition.to?.name.startsWith('designer.board.block')) {
				try {
					toBlock = (transition.to.params?.block_id as string) || '';
				} catch (_e) {
					// skip
				}
			}

			// show resolve data popup if needed
			const showResolvePopup = () => {
				const modelObject = this.mmBoardsService.findModel(model.id!);
				if (modelObject?.status === BoardStatus.ERROR_RESOLUTION_CENTER) {
					this.mmBoardsService.openResolveDataPopup({ model: modelObject });
				}
			};

			const afterLoadingBoardData = () => {
				const isInDesigner =
					this.navigationService.activatedRoute.startsWith('designer.board');

				// if we aren't on same board, don't transition anywhere
				const designerBoardModel = this.modelFor('designer.board') as BoardRecord;
				if (toBlock && isInDesigner && designerBoardModel.id === boardId) {
					this.navigationService.replaceRoute('designer.board.block', model, toBlock);
					const designerBoardBlockController = this.controllerFor(
						'designer.board.block',
					) as DesignerBoardBlockController;
					next(() => {
						designerBoardBlockController.boardDataLoaded(boardId);
					});
				}
				showResolvePopup();

				// in 10s, check for opening AI summary autocreate popup
				later(() => {
					const isInDesignerNow =
						this.navigationService.activatedRoute.startsWith('designer.board');
					const designerBoardModelNow = this.modelFor('designer.board') as BoardRecord;
					if (isInDesignerNow && designerBoardModelNow.id === boardId) {
						this.mmBlocksService.checkToOpenAiSummaryAutocreatePopup(
							this.mmBoardsService.findModel(boardId)!,
							(this.controllerFor('designer') as DesignerController)
								.activeDatawallGrid || undefined,
						);
					}
				}, 10 * 1000);
			};

			void this.mmBoardsService.requestBoardData(boardId).then(() => {
				later(afterLoadingBoardData, 1);
			});
		}

		void this.mmMetricsService.prepareForMetricEditing();
	}

	override setupController(
		controller: DesignerBoardController,
		model: BoardRecord,
		transition: PublicTransition,
	): void {
		super.setupController(controller, model, transition);

		// triggering getter to update board on designer controller

		const boardModel = controller.board;
		if (boardModel) {
			next(() => {
				(this.controllerFor('designer') as DesignerController).updateBoard(boardModel);
			});
		}

		const tourId = TourId.DESIGNER__INTRO;
		this.flowTourDesignerService.bindTourCheck(tourId, this, () => {
			this.flowTourDesignerService.startTour(tourId);
		});

		this.eventManagerService.on(AppEvent.DESIGNER_BOARD_LOADED, controller, (data) => {
			if (data.board) {
				next(controller, () => {
					this.flowTourDesignerService.checkTour();
				});
			}
			return true; // just once
		});
	}

	override resetController(controller: DesignerBoardController): void {
		this.eventManagerService.off(AppEvent.DESIGNER_BOARD_LOADED, controller);
		this.flowTourDesignerService.unbindTourCheck(TourId.DESIGNER__INTRO, this);
	}
}
